/**
 * version-tracking — zachytí verze serveru při načtení stránky (baseline),
 * aby UpdateAvailableToast mohl po deployi zobrazit "z verze X na verzi Y".
 *
 * Mechanika:
 *   - Při startu appky `captureBaselineVersions()` fetchne `/healthz` a uloží
 *     api+web verze do sessionStorage (jen jednou per tab session).
 *   - To je "současná" verze = stav serveru v okamžiku, kdy se načetl běžící bundle.
 *   - Po deployi serveru SW vyšle `controllerchange` → toast fetchne `/healthz`
 *     znovu (= "nová" verze) a porovná s baseline.
 *
 * Pozn.: web verzi bereme z `/healthz` (versions.web = verze nasazená na serveru),
 * NE z build-time `VITE_APP_VERSION` — healthz je konzistentní zdroj pro obě verze
 * a v dev modu vrací skutečné package.json hodnoty, ne "dev".
 */

const BASELINE_KEY = "bw:versions-baseline";

export interface VersionPair {
  api: string;
  web: string;
}

// ── Build identita (anti-falešná-aktualizace) ─────────────────────────────────
// Zapečené v bundle při buildu (viz vite.config.ts `define`). Reprezentují PRÁVĚ
// běžící bundle — pravdivý zdroj nezávislý na cache `/healthz`.
export const RUNNING_BUILD_ID: string =
  typeof __APP_BUILD_ID__ === "string" ? __APP_BUILD_ID__ : "dev";
export const RUNNING_WEB: string =
  typeof __APP_WEB_VERSION__ === "string" ? __APP_WEB_VERSION__ : "dev";

export interface DeployedBuild {
  buildId: string;
  web: string;
  builtAt: string;
}

/**
 * Načte identitu PRÁVĚ NASAZENÉHO buildu z `/version.json` (mimo SW precache,
 * `no-store`). Porovnáním s `RUNNING_BUILD_ID` se pozná, zda `controllerchange`
 * znamená reálnou změnu bundle (gate proti falešné nabídce aktualizace).
 */
export async function fetchDeployedBuild(): Promise<DeployedBuild | null> {
  try {
    const res = await fetch("/version.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as Partial<DeployedBuild>;
    if (typeof json.buildId !== "string") return null;
    return {
      buildId: json.buildId,
      web: typeof json.web === "string" ? json.web : "?.?.?",
      builtAt: typeof json.builtAt === "string" ? json.builtAt : "",
    };
  } catch {
    return null;
  }
}

interface HealthzVersions {
  versions?: { api?: string; web?: string };
}

/** Fetchne aktuální verze ze serveru přes /healthz. null při chybě. */
export async function fetchServerVersions(): Promise<VersionPair | null> {
  try {
    const res = await fetch("/healthz", { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const json = (await res.json()) as HealthzVersions;
    return {
      api: json.versions?.api ?? "?.?.?",
      web: json.versions?.web ?? "?.?.?",
    };
  } catch {
    return null;
  }
}

// ── Changelog („Co je nového") ────────────────────────────────────────────────

export interface ChangelogEntry {
  date: string;
  web: string | null;
  api: string | null;
  syncWorker: string | null;
  shared: string | null;
  summary: string;
}

/** Fetchne changelog ze serveru (`/api/changelog`). Prázdné pole při chybě. */
export async function fetchChangelog(): Promise<ChangelogEntry[]> {
  try {
    const res = await fetch("/api/changelog", { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const json = (await res.json()) as { entries?: unknown };
    return Array.isArray(json.entries) ? (json.entries as ChangelogEntry[]) : [];
  } catch {
    return [];
  }
}

/** `a > b` v semveru (jen číselné `x.y.z`; nevalidní → false). */
export function versionGt(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return false;
  for (let i = 0; i < 3; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db;
  }
  return false;
}

/**
 * Záznamy novější než verze, kterou uživatel běží — záznam je „nový", pokud jeho
 * `web` verze je vyšší než běžící web (`runningWeb`) NEBO jeho `api` verze je vyšší
 * než běžící api (`baselineApi`, zachycené při startu). To je „Co je nového"
 * od poslední verze uživatele po právě nasazenou.
 */
export function newerEntries(
  entries: ChangelogEntry[],
  runningWeb: string,
  baselineApi: string | null,
): ChangelogEntry[] {
  return entries.filter(
    (e) => versionGt(e.web, runningWeb) || versionGt(e.api, baselineApi),
  );
}

/**
 * Zachytí baseline verze při startu appky — jen jednou per tab session.
 * Pokud už baseline existuje (appka se remountla), neuloží znovu.
 */
export async function captureBaselineVersions(): Promise<void> {
  try {
    if (sessionStorage.getItem(BASELINE_KEY)) return; // už zachyceno
    const current = await fetchServerVersions();
    if (current) {
      sessionStorage.setItem(BASELINE_KEY, JSON.stringify(current));
    }
  } catch {
    // sessionStorage nedostupný (private mode) — toast prostě baseline nezobrazí
  }
}

/** Vrátí baseline verze zachycené při startu, nebo null. */
export function getBaselineVersions(): VersionPair | null {
  try {
    const raw = sessionStorage.getItem(BASELINE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<VersionPair>;
    if (typeof parsed.api === "string" && typeof parsed.web === "string") {
      return { api: parsed.api, web: parsed.web };
    }
    return null;
  } catch {
    return null;
  }
}
